<template>
  <div class="py-md px-xl h-full min-h-0 flex flex-col">
    <template v-if="isEmpty && !docsLoading">
      <EmptyDocs />
    </template>
    <template v-else-if="docsLoading">
      <div
        class="flex flex-col items-center content-center justify-around h-full"
        data-test="docs-loading-block"
      >
        <OrLoaderV3
          :title="$t('home.loading')"
          :description="$t('home.loadingSecondaryText')"
          variant="linear"
        />
      </div>
    </template>
    <template v-else>
      <div class="flex pb-md items-center flex-row justify-between gap-md flex-wrap">
        <OrSortingV3
          v-model="sort"
          :options="sortOptions"
          data-test="sorting-button"
          @update:modelValue="handleSortChange"
        />
        <OrInputV3
          class="md:w-[350px]"
          size="m"
          type="search"
          placeholder="Search"
          data-test="projects-search"
          @update:modelValue="filter = String($event)"
        />
      </div>

      <EmptySearchResult
        v-if="isNoResult"
        :search-text="filter"
      />

      <div
        v-else
        class="collection pb-md overflow-auto grow min-h-0 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-md auto-rows-max"
      >
        <ProjectItem
          v-for="item in items"
          :key="item.projectName"
          v-bind="item"
          @versionDelete="onVersionDelete"
        />
      </div>
    </template>

    <delete-version-modal
      :is-opened="isDeleteModalOpened"
      :is-loading="isDeleteVersionLoading"
      :version="forDelete?.version"
      :confirm-text="confirmCode"
      @confirm="deleteVersion"
      @close="closeDeleteModal"
    />
  </div>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import useDocsStore from '@/stores/Docs.store';
import {
  OrInputV3,
  OrLoaderV3,
  OrSortingV3,
} from '@onereach/ui-components';
import type { SortOption } from '@onereach/ui-components';
import { storeToRefs } from 'pinia';
import { ProjectItem, DeleteVersionModal, EmptyDocs, EmptySearchResult } from '@/components';
import { useToggle } from '@vueuse/core';
import useProjectVersionStore from '@/stores/Version.store';
import i18n from '@/i18n/i18n';

const projectsStore = useDocsStore();
const { deleteProjectVersion } = useProjectVersionStore();
const [isDeleteModalOpened, toggleDeleteModal] = useToggle(false);
const [isDeleteVersionLoading, toggleDeleteVersionLoading] = useToggle(false);
const {
  getDocAsMenuItems,
  docsLoading,
} = storeToRefs(projectsStore);

const sort = ref('asc');
const filter = ref('');
const confirmCode = ref<string | null>(null);
const forDelete = ref<Record<'project'| 'version', string> | null>(null);

const sortOptions = computed<SortOption[]>(() => [
  {
    value: 'asc',
    label: i18n.global.t('asc'),
  },
  {
    value: 'desc',
    label: i18n.global.t('desc'),
  },
]);
const localValues = computed(() => getDocAsMenuItems.value.map(i => ({
  ...i,
  vforkey: i.projectName,
})));
const isEmpty = computed(() => localValues.value.length === 0);
const items = computed(() => [...localValues.value].sort(
  (a, b) => sort.value === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label),
).filter(i => i.label.toLowerCase().includes(filter.value.toLowerCase())));
const isNoResult = computed(() => !isEmpty.value && !docsLoading.value && !items.value.length);

function generateCode() {
  confirmCode.value = `${Math.floor(1000 + Math.random() * 9000)}`;
}

function handleSortChange(newSort: string) {
  sort.value = newSort;
}

function onVersionDelete(e: {version: string; project: string;}) {
  generateCode();
  forDelete.value = e;
  toggleDeleteModal();
}

async function deleteVersion() {
  toggleDeleteVersionLoading(true);
  const { version, project } = forDelete.value as Record<'project'| 'version', string>;
  await deleteProjectVersion(project, version);
  closeDeleteModal();
}

function closeDeleteModal() {
  forDelete.value = null;
  confirmCode.value = null;
  toggleDeleteModal(false);
  toggleDeleteVersionLoading(false);
}
</script>
